import {
    Component, ChangeDetectionStrategy, inject, signal, computed, OnInit, output
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';

import { AuthService } from '../../../core/services/auth.service';
import { UserService } from '../../../core/services/user.service';
import { SnackbarService } from '../../../core/services/snackbar.service';

export type SettingsTab = 'secure-pin' | 'password';

// Custom validator: confirm field matches another field
function matchValidator(matchField: string) {
    return (control: AbstractControl): ValidationErrors | null => {
        const parent = control.parent;
        if (!parent) return null;
        const target = parent.get(matchField);
        return target && control.value !== target.value ? { mismatch: true } : null;
    };
}

@Component({
    selector: 'fk-user-settings-modal',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatIconModule
    ],
    templateUrl: './user-settings-modal.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserSettingsModalComponent implements OnInit {
    private fb = inject(FormBuilder);
    authService = inject(AuthService);
    private userService = inject(UserService);
    private snackbarService = inject(SnackbarService);

    /** Emit when user clicks X to close */
    closed = output<void>();

    closeRequested(): void {
        this.closed.emit();
    }

    /** Active tab */
    activeTab = signal<SettingsTab>('secure-pin');

    /** Loading / saving states */
    isSavingPin = signal(false);
    isSavingPassword = signal(false);

    /** Password visibility toggles */
    showCurrentPassword = signal(false);
    showNewPin = signal(false);
    showConfirmPin = signal(false);
    showCurrentPwd = signal(false);
    showNewPwd = signal(false);
    showConfirmPwd = signal(false);

    // ── Change Secure PIN form ─────────────────────────────────────────────
    pinForm = this.fb.group({
        currentPassword: ['', [Validators.required]],
        newPin: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
        confirmPin: ['', [Validators.required, Validators.pattern(/^\d{6}$/), matchValidator('newPin')]]
    });

    // ── Change Password form ───────────────────────────────────────────────
    passwordForm = this.fb.group({
        currentPassword: ['', [Validators.required]],
        newPassword: ['', [Validators.required, Validators.minLength(8)]],
        confirmPassword: ['', [Validators.required, matchValidator('newPassword')]]
    });

    /** Computed initials from the logged-in user */
    userInitials = computed(() => {
        const name = this.authService.user()?.name || '';
        const parts = name.trim().split(/\s+/);
        return parts.length >= 2
            ? (parts[0][0] + parts[1][0]).toUpperCase()
            : name.slice(0, 2).toUpperCase();
    });

    /** Role label */
    roleLabel = computed(() => {
        const role = this.authService.user()?.role || '';
        const found = this.userService.allRoles().find(r => r.role_code === role);
        const fallbacks: Record<string, string> = {
            PLATFORM_SUPER_ADMIN: 'Platform Super Admin',
            ORG_ADMIN: 'Admin',
            CS: 'CS',
            OPS: 'OPS',
            CUSTOMER: 'Customer'
        };
        return found?.role_name || fallbacks[role] || role;
    });

    ngOnInit(): void {
        // Restrict newPin to max 6 digits numeric and trigger confirmPin re-validation
        this.pinForm.get('newPin')?.valueChanges.subscribe(val => {
            if (val) {
                const cleaned = val.replace(/\D/g, '').slice(0, 6);
                if (cleaned !== val) {
                    this.pinForm.get('newPin')?.setValue(cleaned, { emitEvent: false });
                }
            }
            this.pinForm.get('confirmPin')?.updateValueAndValidity();
        });

        // Restrict confirmPin to max 6 digits numeric
        this.pinForm.get('confirmPin')?.valueChanges.subscribe(val => {
            if (val) {
                const cleaned = val.replace(/\D/g, '').slice(0, 6);
                if (cleaned !== val) {
                    this.pinForm.get('confirmPin')?.setValue(cleaned, { emitEvent: false });
                }
            }
        });

        this.passwordForm.get('newPassword')?.valueChanges.subscribe(() => {
            this.passwordForm.get('confirmPassword')?.updateValueAndValidity();
        });
    }

    selectTab(tab: SettingsTab): void {
        this.activeTab.set(tab);
    }

    // ── Secure PIN Save ───────────────────────────────────────────────────
    savePin(): void {
        if (this.pinForm.invalid) {
            this.pinForm.markAllAsTouched();
            return;
        }

        const user = this.authService.user();
        if (!user) return;

        this.isSavingPin.set(true);
        const raw = this.pinForm.value;

        this.userService.resetSecurePin({
            user_id: user.id.toString(),
            secure_pin: raw.newPin || '',
            password: raw.currentPassword || ''
        }).subscribe({
            next: (res) => {
                this.isSavingPin.set(false);
                this.snackbarService.show({ type: 'success', message: res?.message || 'Secure PIN updated successfully.' });
                this.pinForm.reset();
            },
            error: (err) => {
                this.isSavingPin.set(false);
                this.snackbarService.show({ type: 'error', message: err?.error?.message || 'Failed to update Secure PIN.' });
            }
        });
    }

    clearPin(): void {
        this.pinForm.reset();
    }

    // ── Change Password Save ──────────────────────────────────────────────
    savePassword(): void {
        if (this.passwordForm.invalid) {
            this.passwordForm.markAllAsTouched();
            return;
        }

        this.isSavingPassword.set(true);
        const raw = this.passwordForm.value;

        this.userService.changePassword({
            old_password: raw.currentPassword || '',
            new_password: raw.newPassword || ''
        }).subscribe({
            next: (res) => {
                this.isSavingPassword.set(false);
                this.snackbarService.show({ type: 'success', message: res?.message || 'Password changed successfully' });
                this.passwordForm.reset();
            },
            error: (err) => {
                this.isSavingPassword.set(false);
                this.snackbarService.show({ type: 'error', message: err?.error?.message || 'Failed to change password.' });
            }
        });
    }

    clearPassword(): void {
        this.passwordForm.reset();
    }

    // ── Error helpers ─────────────────────────────────────────────────────
    pinError(field: string): string | null {
        const ctrl = this.pinForm.get(field);
        if (!ctrl?.touched || ctrl.valid) return null;
        if (ctrl.hasError('required')) return `${this.fieldLabel(field)} is required`;
        if (ctrl.hasError('pattern')) return 'PIN must be exactly 6 digits';
        if (ctrl.hasError('mismatch')) return 'PINs do not match';
        return null;
    }

    passwordError(field: string): string | null {
        const ctrl = this.passwordForm.get(field);
        if (!ctrl?.touched || ctrl.valid) return null;
        if (ctrl.hasError('required')) return `${this.fieldLabel(field)} is required`;
        if (ctrl.hasError('minlength')) return 'Password must be at least 8 characters';
        if (ctrl.hasError('mismatch')) return 'Passwords do not match';
        return null;
    }

    private fieldLabel(field: string): string {
        const labels: Record<string, string> = {
            currentPassword: 'Current password', newPin: 'New PIN', confirmPin: 'Confirm PIN',
            newPassword: 'New password', confirmPassword: 'Confirm password'
        };
        return labels[field] || field;
    }
}

